// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Federal Bills Browser — browse all cached federal bills with client-side
 * filtering, sorting, and pagination via DataTable.
 */
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Loader2 } from 'lucide-react'

import { useServices } from '@/app-shell/services'
import { useWorkspaceConfig } from '@/app-shell/hooks'
import { useAgents, useWorkspaceRenderers } from '@/subsystems/agents'
import type { NodeUserViewProps } from '@/core/types.ts'

import type { FederalBillMetadata } from '../models/types.ts'
import { fetchFederalBills, fetchFederalBillSummaries, type BillSummariesMap, type BillSummaryEntry } from '../services/federal-bills.service.ts'
import { useFederalBillsColumns } from '../hooks/useFederalBillsColumns.tsx'
import { BillSummaryDialog } from '../components/dialogs/BillSummaryDialog.tsx'


import { useBillsCache } from '../hooks/useBillsCache.ts'
import { useAnalysisStatuses } from '@/applications/shared'
import { useRendererColumns } from '../hooks/useRendererColumns.tsx'
import { usePipelineJobTracker } from '../hooks/usePipelineJobTracker.ts'
import { BillsBrowserFilters } from '../components/BillsBrowserFilters.tsx'
import { BillsCacheContent } from '../components/BillsCacheContent.tsx'

const PAGE_SIZE = 50

export const FederalBillsBrowser: FC<NodeUserViewProps> = ({
  orgId,
  workspaceId,
  onLoadStart,
  onLoadEnd,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  // Datasources come from workspace config; agents (and their renderers and
  // run actions) are aggregated from every agent in the workspace.
  const { applicationSettings } = useWorkspaceConfig(orgId, workspaceId)
  const renderers = useWorkspaceRenderers(apiClient, orgId ?? '', workspaceId ?? '')
  // Every agent with a pipeline becomes a "Run <agent>" action in the row menu.
  const { data: agents } = useAgents(apiClient, orgId ?? '', workspaceId ?? '')
  const availableAgents = useMemo(
    () =>
      (agents ?? [])
        .filter((a) => Boolean(a.pipelineId))
        .map((a) => ({ agentId: a.agentId, label: a.label, pipelineId: a.pipelineId! })),
    [agents],
  )
  // Find the federal datasource and extract congress numbers from sessions
  const federalDs = useMemo(
    () => (applicationSettings?.datasources ?? []).find((ds) => ds.id.toLowerCase().startsWith('federal')),
    [applicationSettings?.datasources],
  )
  const congressOptions = useMemo(() => {
    if (!federalDs) return []
    const congresses = new Set<number>()
    for (const session of federalDs.sessions) {
      const match = session.match(/^(\d+)/)
      if (match) congresses.add(parseInt(match[1], 10))
    }
    return Array.from(congresses).sort((a, b) => b - a)
  }, [federalDs])

  const [congress, setCongress] = useState<number | null>(null)
  const [search, setSearch] = useState('')

  // Default to first (latest) congress when options load
  useEffect(() => {
    if (congressOptions.length > 0 && congress === null) {
      setCongress(congressOptions[0])
    }
  }, [congressOptions, congress])

  const sessionStr = congress !== null ? String(congress) : null

  // --- Shared hooks ---

  const fetchBillsFn = useCallback(
    () => fetchFederalBills(apiClient, orgId, workspaceId, congress!),
    [apiClient, orgId, workspaceId, congress],
  )

  const cache = useBillsCache<FederalBillMetadata>({
    apiClient,
    datasource: 'federal',
    session: sessionStr,
    fetchBills: fetchBillsFn,
    onLoadStart, onLoadEnd,
  })

  // The datasets these columns display, each tagged with its owning agent — the
  // agent scopes the fetch, and a single-document refresh needs the keys too.
  const datasets = useMemo(
    () => Object.values(renderers ?? {}).flatMap(r =>
      r.slots.flatMap(slot => slot.type === 'dataset' && slot.datasetKey
        ? [{ agentId: r.agentId, datasetKey: slot.datasetKey }]
        : []),
    ),
    [renderers],
  )
  const agentIds = useMemo(
    () => Array.from(new Set(datasets.map(d => d.agentId))),
    [datasets],
  )

  const analysisStatuses = useAnalysisStatuses({
    apiClient, orgId, workspaceId,
    agentIds,
    groupId: sessionStr ?? undefined,
    enabled: congress !== null,
  })
  // Score analysis dialog
  // Dynamic columns from renderer definitions
  const getDocId = useCallback(
    (bill: FederalBillMetadata) => congress ? `${congress}-${bill.type}-${bill.number}` : '',
    [congress],
  )
  const getBillNumber = useCallback(
    (bill: FederalBillMetadata) => `${bill.type.toUpperCase()}-${bill.number}`,
    [],
  )

  const rendererColumns = useRendererColumns<FederalBillMetadata>({
    apiClient, orgId, workspaceId,
    groupId: sessionStr ?? undefined,
    renderers, analysisStatuses, getDocId, getBillNumber,
  })

  // --- Federal-specific state ---

  // Bill summaries (CRS summaries from govinfo) — keyed by "type-number"
  const [summariesMap, setSummariesMap] = useState<BillSummariesMap>(new Map())
  const summaryKeys = useMemo(() => new Set(summariesMap.keys()), [summariesMap])

  const refreshDocumentStatus = useCallback((docId: string) => {
    analysisStatuses.refreshDocument(docId, datasets)
  }, [analysisStatuses, datasets])

  const jobTracker = usePipelineJobTracker({
    apiClient, orgId, workspaceId,
    onScoreJobComplete: refreshDocumentStatus,
    onReviewJobComplete: refreshDocumentStatus,
  })

  // Track selected version per bill (bill key -> version number)
  const [selectedVersions, setSelectedVersions] = useState<Map<string, number>>(new Map())
  const handleVersionChange = useCallback((bill: FederalBillMetadata, versionNumber: number) => {
    const key = `${bill.type}-${bill.number}`
    setSelectedVersions(prev => new Map(prev).set(key, versionNumber))
  }, [])

  const handleReviewBill = useCallback((bill: FederalBillMetadata, pipelineId: string) => {
    if (!congress) return
    const key = `${bill.type}-${bill.number}`
    const billNumber = `${congress}-${bill.type}-${bill.number}`.toUpperCase()

    // Score pipelines use latest version (backend resolves); review pipelines use selected version
    const isScorePipeline = pipelineId.startsWith('score-')
    const versions = bill.textVersions
    const selectedNum = selectedVersions.get(key)
    const version = isScorePipeline
      ? undefined
      : selectedNum ?? (versions && versions.length > 0 ? versions[versions.length - 1].versionNumber : undefined)

    jobTracker.runPipeline({
      billKey: key,
      billNumber,
      sessionCode: String(congress),
      pipelineId,
      version,
    })
  }, [congress, selectedVersions, jobTracker])

  // Fetch all summaries for this congress (single S3 download)
  useEffect(() => {
    if (!congress || cache.bills.length === 0) return
    let cancelled = false
    fetchFederalBillSummaries(apiClient, orgId, workspaceId, congress)
      .then((res) => {
        if (!cancelled) setSummariesMap(res)
      })
      .catch((err) => {
        if (!cancelled) console.warn('Failed to fetch summaries:', err)
      })
    return () => { cancelled = true }
  }, [apiClient, orgId, workspaceId, congress, cache.bills])

  const [loadingPdfBill] = useState<string | null>(null)

  // Dialog state for viewing bill summary
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false)
  const [selectedSummaries, setSelectedSummaries] = useState<BillSummaryEntry[]>([])
  const [selectedSummaryBillNumber, setSelectedSummaryBillNumber] = useState('')

  const handleViewSummary = useCallback((bill: FederalBillMetadata) => {
    const key = `${bill.type.toLowerCase()}-${bill.number}`
    const allEntries = summariesMap.get(key) ?? []
    const latest = allEntries.length > 0 ? [allEntries[allEntries.length - 1]] : []
    setSelectedSummaryBillNumber(`${bill.type.toUpperCase()}-${bill.number}`)
    setSelectedSummaries(latest)
    setSummaryDialogOpen(true)
  }, [summariesMap])

  // Client-side search + score/analysis filters
  const filteredBills = useMemo(() => {
    let result = cache.bills
    if (search.trim()) {
      const term = search.toLowerCase()
      result = result.filter(
        (b) =>
          `${b.type}-${b.number}`.toLowerCase().includes(term) ||
          b.title.toLowerCase().includes(term),
      )
    }
    return rendererColumns.applyFilters(result)
  }, [cache.bills, search, rendererColumns])

  const baseColumns = useFederalBillsColumns({
    selectedVersions,
    onVersionChange: handleVersionChange,
    loadingPdfBill,
    agents: availableAgents,
    onRunAgent: (bill, pipelineId) => void handleReviewBill(bill, pipelineId),
    reviewStatuses: jobTracker.statuses,
    congress,
    summaryKeys,
    onViewSummary: handleViewSummary,
  })

  // Merge renderer columns before the actions column (last column)
  const columns = useMemo(() => {
    if (rendererColumns.columns.length === 0) return baseColumns
    const actions = baseColumns[baseColumns.length - 1]
    return [...baseColumns.slice(0, -1), ...rendererColumns.columns, actions]
  }, [baseColumns, rendererColumns.columns])

  return (
    <div className="space-y-4" data-testid="lr-federal-browser">
      <Card>
        <CardHeader className="space-y-4">
          {/* Title row */}
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Federal Bills Browser</CardTitle>
              <CardDescription>
                Browse all cached federal bills from govinfo.gov
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {congressOptions.length > 0 && (
                <Select
                  value={congress !== null ? String(congress) : ''}
                  onValueChange={(v) => setCongress(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-40" data-testid="lr-federal-congress-select">
                    <SelectValue placeholder="Congress" />
                  </SelectTrigger>
                  <SelectContent>
                    {congressOptions.map((c) => (
                      <SelectItem key={c} value={String(c)}>
                        {c}th Congress
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Filters row */}
          <BillsBrowserFilters
            search={search}
            onSearchChange={setSearch}
            testIdPrefix="lr-federal"
          />
        </CardHeader>
        <CardContent>
          <BillsCacheContent
            testIdPrefix="lr-federal"
            cacheStatus={cache.cacheStatus}
            loading={cache.loading}
            error={cache.error}
            totalBills={cache.bills.length}
            filteredBills={filteredBills}
            columns={columns}
            pageSize={PAGE_SIZE}
            refreshingMessage="Building federal bills cache from govinfo.gov — this may take a couple minutes..."
            loadingMessage="Loading federal bills..."
            statusExtra={analysisStatuses.loading ? (
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading analyses…
              </span>
            ) : undefined}
          />
        </CardContent>
      </Card>
      <BillSummaryDialog
        open={summaryDialogOpen}
        onOpenChange={setSummaryDialogOpen}
        summaries={selectedSummaries}
        billNumber={selectedSummaryBillNumber}
        testId="lr-federal-summary-dialog"
      />
      {rendererColumns.dialog}
    </div>
  )
}
